import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Monitor, Smartphone, Tablet, HelpCircle, LogOut, ShieldCheck, Clock, MapPin, CheckCircle2, XCircle,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Panel, Badge2 } from "@/components/Bits";
import { useMeta } from "@/lib/useMeta";

const DEVICE_ICON = { Desktop: Monitor, Mobile: Smartphone, Tablet: Tablet };

const when = (v) =>
  v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const ago = (v) => {
  if (!v) return "";
  const mins = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
};

export default function Settings() {
  useMeta({
    title: "Account Security | Plan-Sarthi",
    description: "Review your Plan-Sarthi login history and active devices, and sign out everywhere in one tap.",
    path: "/settings",
  });
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/auth/security-log")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const signOutEverywhere = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/auth/sign-out-everywhere");
      toast.success(
        res.signed_out
          ? `Signed out of ${res.signed_out} other device${res.signed_out === 1 ? "" : "s"}`
          : "No other devices were signed in"
      );
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const others = (data?.sessions || []).filter((s) => !s.current).length;

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <header className="border-b border-border sticky top-0 z-30 bg-background/90 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center gap-3">
          <Link
            to="/projects"
            className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors duration-150"
            data-testid="settings-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Portfolio
          </Link>
          <span className="ml-auto text-[12px] text-muted-foreground truncate">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">Account security</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Everywhere you have signed in, and every attempt on your account.
          </p>
        </div>

        <nav className="flex gap-1 border-b border-border" data-testid="settings-tabs">
          <span
            className="px-3 py-2 text-[13px] font-medium border-b-2 border-primary"
            data-testid="settings-tab-security"
          >
            Security
          </span>
        </nav>

        {!data ? (
          <p className="text-sm text-muted-foreground">Loading your security activity…</p>
        ) : (
          <>
            <section
              className="border border-border bg-card p-4 flex flex-wrap items-center gap-4"
              data-testid="sign-out-everywhere-card"
            >
              <ShieldCheck className="w-8 h-8 text-primary shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <h2 className="text-sm font-semibold">Sign out everywhere</h2>
                <p className="text-[12.5px] text-muted-foreground mt-0.5">
                  {others > 0
                    ? `${others} other device${others === 1 ? "" : "s"} currently signed in. This device stays signed in.`
                    : "No other devices are signed in right now."}
                  {data.single_session
                    ? " Your account already allows only one active session at a time."
                    : ""}
                </p>
              </div>
              <Button
                onClick={signOutEverywhere}
                disabled={busy || others === 0}
                variant="destructive"
                data-testid="sign-out-everywhere-btn"
              >
                <LogOut className="w-4 h-4 mr-1.5" />
                {busy ? "Signing out…" : "Sign out everywhere"}
              </Button>
            </section>

            <Panel title={`Active devices (${data.sessions.length})`} testId="active-devices-panel">
              {data.sessions.length === 0 ? (
                <p className="text-[12.5px] text-muted-foreground">No active sessions.</p>
              ) : (
                <ul className="divide-y divide-border -m-3">
                  {data.sessions.map((s) => {
                    const Icon = DEVICE_ICON[s.device] || HelpCircle;
                    return (
                      <li
                        key={`${s.created_at}-${s.ip}`}
                        className="flex items-center gap-3 px-3 py-3"
                        data-testid="active-device-row"
                      >
                        <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[13px] font-medium">{s.device}</span>
                            {s.current ? (
                              <Badge2 tone="good" testId="current-device-badge">This device</Badge2>
                            ) : null}
                          </div>
                          <div className="text-[11.5px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {s.ip}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> signed in {when(s.created_at)}
                            </span>
                            <span>last active {ago(s.last_seen)}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border">
                Sessions end automatically after {data.idle_hours} hours of inactivity.
              </p>
            </Panel>

            <Panel title="Login history" testId="login-history-panel">
              {data.logins.length === 0 ? (
                <p className="text-[12.5px] text-muted-foreground">Nothing recorded yet.</p>
              ) : (
                <ul className="divide-y divide-border -m-3">
                  {data.logins.map((l) => (
                    <li
                      key={l.id || `${l.at}-${l.ip}`}
                      className="flex items-center gap-3 px-3 py-2.5"
                      data-testid="login-history-row"
                    >
                      {l.result === "success" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px]">
                          {l.result === "success" ? "Signed in" : "Failed sign-in"} · {l.device}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {l.ip} · {when(l.at)}
                        </div>
                      </div>
                      <span className="text-[11px] text-muted-foreground shrink-0">{ago(l.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <p className="text-[11.5px] text-muted-foreground">
              Something you don&apos;t recognise?{" "}
              <Link to="/forgot-password" className="text-primary hover:underline" data-testid="settings-reset-link">
                Reset your password
              </Link>{" "}
              — that signs every device out immediately.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
